import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import { EzUserRole } from '/ezlibrary/EzUserRole.js';

import {
    EzString,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';

/*
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * IMPORTANT:
 * Please keep this file in sync with the com.ezclocker.enumerations.EzClockerViewId.java
 * java class.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * Defines the known feature toggle ids
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *     import {
 *         // < other enumeration classes ... >
 *         EzClockerViewId
 *     } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into enumeration classes with:
 *     import { EzClockerViewId } from '/ezlibrary/enums/EzClockerViewId.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access static method/properties only:
 *     EzClockerViewId.{property or method name}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzClockerViewId extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Example static method that returns the enumeration property's data.display value.
     * If the enumPropertyValue is not a valid enumeration property value, then EzEnumTemplate.UNKNOWN.display is returned.
     * @param {undefined|null|string} enumPropertyValue
     * A valid enumeration property value from EzEnumTemplate
     * @returns {string}
     */
    static ezToDisplayName(enumPropertyValue) {
        return EzString.stringOrEmpty(
            EzString.hasLength(enumPropertyValue)
                ? EzEnumTemplate.ezEnumData(enumPropertyValue)?.display
                : EzEnumTemplate.ezEnumData(EzEnumTemplate.UNKNOWN)?.display);
    }

    /**
     * @static
     * @public @function
     * Gets the EzClockerViewId enumeration property name for a given enumeration property name, value, or legacy name.
     * @param {undefined|null|string} enumPropertyNameValueOrLegacyName
     * A valid enumeration property value, name, or legacy name from EzClockerViewId
     * Default: EzClockerViewId.UNKNOWN
     * @returns {string}
     * A valid enumeration property value from EzClockerViewId
     */
    static ezViewId(enumPropertyNameValueOrLegacyName = EzClockerViewId.UNKNOWN) {
        if (EzString.hasText(enumPropertyNameValueOrLegacyName)) {
            const enumPropertyNameValueOrLegacyNameUpperCase = enumPropertyNameValueOrLegacyName.toUpperCase();

            // Check if it matches any of enumeration property names
            for (const enumPropName in EzClockerViewId.ezNames) {
                if (enumPropName.toUpperCase() === enumPropertyNameValueOrLegacyNameUpperCase ||
                    EzClockerViewId[enumPropName].toUpperCase() === enumPropertyNameValueOrLegacyNameUpperCase) {
                    return EzClockerViewId.ezNameOf(enumPropName);
                }

                const enumData = EzClockerViewId.ezEnumData(EzClockerViewId[enumPropName]);

                if (EzArray.hasLength(enumData?.legacyEnumNames)) {
                    for (const legacyEnumName of enumData?.legacyEnumNames) {
                        if (legacyEnumName.toUpperCase() === enumPropertyNameValueOrLegacyNameUpperCase) {
                            return EzClockerViewId.ezNameOf(enumData?.activeEnumName);
                        }
                    }
                }
            }
        }

        return EzClockerViewId.UNKNOWN;
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Initialization Section: ONLY PLACE INITIALIZATION CODE BEYOND THIS SECTION
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzEnumTemplate}
     * Internal static instance of the EzEnumTemplate
     */
    static #ezClockerViewIdEnumerationSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezBuildEnumeration() {
        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Unknown Features
         * Feature Group: ezfgUnknownFeatures
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            'UNKNOWN',
            // Optional Enumeration Data Value
            {
                name: 'Unknown View',
                activeViewName: 'UNKNOWN',
                legacyEnumNames: EzArray.EMPTY,
                REQUIRED_ROLES: EzArray.EMPTY,
                publicView: false,
                personalView: false,
                employeeView: false,
                managerView: false,
                payrollManagerView: false,
                employerView: false,
                supportView: false,
                adminView: false
            });

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Team Chat Dialog
         * View Id: ezTeamChatDialog
         * Legacy View Ids: teamChatView, TEAM_CHAT_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezTeamChatDialogData = {
            name: 'Team Chat Dialog',
            activeEnumName: 'ezTeamChatDialog',
            legacyEnumNames: [
                'teamChatView',
                'TEAM_CHAT_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EMPLOYEE,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT
            ],
            publicView: false,
            personalView: false,
            employeeView: true,
            managerView: true,
            payrollManagerView: true,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezTeamChatDialog',
            'ezTeamChatDialog',
            ezTeamChatDialogData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'teamChatView',
            'teamChatView',
            ezTeamChatDialogData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'TEAM_CHAT_VIEW',
            'TEAM_CHAT_VIEW',
            ezTeamChatDialogData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employer Dashboard View
         * View Id: ezEmployerDashboardView
         * Legacy View Ids: employerDashboardView, employerDashboard, EMPLOYER_DASHBOARD_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmployerDashboardViewData = {
            name: 'Employer Dashboard View',
            activeViewName: 'ezEmployerDashboardView',
            legacyEnumNames: [
                'employerDashboardView',
                'employerDashboard',
                'EMPLOYER_DASHBOARD_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: false,
            managerView: true,
            payrollManagerView: true,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerDashboardView',
            'ezEmployerDashboardView',
            ezEmployerDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerDashboardView',
            'employerDashboardView',
            ezEmployerDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerDashboard',
            'employerDashboard',
            ezEmployerDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYER_DASHBOARD_VIEW',
            'EMPLOYER_DASHBOARD_VIEW',
            ezEmployerDashboardViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employer Schedule View
         * View Id: ezEmployerScheduleView
         * Legacy View Ids: employerSchedule, EMPLOYER_SCHEDULE_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmployerScheduleViewData = {
            name: 'Employer Schedule View',
            activeEnumName: 'ezEmployerScheduleView',
            legacyEnumNames: [
                'employerSchedule',
                'EMPLOYER_SCHEDULE_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: false,
            managerView: true,
            payrollManagerView: true,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerScheduleView',
            'ezEmployerScheduleView',
            ezEmployerScheduleViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerSchedule',
            'employerSchedule',
            ezEmployerScheduleViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYER_SCHEDULE_VIEW',
            'EMPLOYER_SCHEDULE_VIEW',
            ezEmployerScheduleViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employer Archive Employee View
         * View Id: ezEmployerArchiveEmployeeView
         * Legacy View Ids: ezEmployerArchiveView, employerArchiveView, EMPLOYER_ARCHIVE_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmployerArchiveViewData = {
            name: 'Employer Archive Employee View',
            activeEnumName: 'ezEmployerArchiveEmployeeView',
            legacyEnumNames: [
                'ezEmployerArchiveView',
                'employerArchiveView',
                'EMPLOYER_ARCHIVE_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: false,
            managerView: true,
            payrollManagerView: true,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerArchiveEmployeeView',
            'ezEmployerArchiveEmployeeView',
            ezEmployerArchiveViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerArchiveView',
            'ezEmployerArchiveView',
            ezEmployerArchiveViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerArchiveView',
            'employerArchiveView',
            ezEmployerArchiveViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYER_ARCHIVE_VIEW',
            'EMPLOYER_ARCHIVE_VIEW',
            ezEmployerArchiveViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employer Account Details View
         * View Id: ezEmployerAccountDetailsView
         * Legacy View Ids: employerAccountView, account, EMPLOYER_ACCOUNT_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezAccountViewData = {
            name: 'Employer Account Details View',
            activeEnumName: 'ezEmployerAccountDetailsView',
            legacyEnumNames: [
                'employerAccountView',
                'account',
                'EMPLOYER_ACCOUNT_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: false,
            managerView: false,
            payrollManagerView: false,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerAccountDetailsView',
            'ezEmployerAccountDetailsView',
            ezAccountViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerAccountView',
            'employerAccountView',
            ezAccountViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'account',
            'account',
            ezAccountViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYER_ACCOUNT_VIEW',
            'EMPLOYER_ACCOUNT_VIEW',
            ezAccountViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employer Integrations View
         * View Id: ezEmployerIntegrationsView
         * Legacy View Ids: employerIntegrationsView, EMPLOYER_INTEGRATIONS_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmployerIntegrationsViewData = {
            name: 'Employer Integrations View',
            activeEnumName: 'ezEmployerIntegrationsView',
            legacyEnumNames: [
                'employerIntegrationsView',
                'EMPLOYER_INTEGRATIONS_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: false,
            managerView: true,
            payrollManagerView: true,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerIntegrationsView',
            'ezEmployerIntegrationsView',
            ezEmployerIntegrationsViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerIntegrationsView',
            'employerIntegrationsView',
            ezEmployerIntegrationsViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYER_INTEGRATIONS_VIEW',
            'EMPLOYER_INTEGRATIONS_VIEW',
            ezEmployerIntegrationsViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employer Time Off View
         * View Id: ezEmployerTimeOffView
         * Legacy View Ids: employerTimeOffView, EMPLOYER_TIME_OFF_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const employerTimeOffViewData = {
            name: 'Employer Time Off View',
            activeEnumName: 'ezEmployerTimeOffView',
            legacyEnumNames: [
                'employerTimeOffView',
                'EMPLOYER_TIME_OFF_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: false,
            managerView: true,
            payrollManagerView: true,
            employerView: true,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployerTimeOffView',
            'ezEmployerTimeOffView',
            employerTimeOffViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employerTimeOffView',
            'employerTimeOffView',
            employerTimeOffViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYER_TIME_OFF_VIEW',
            'EMPLOYER_TIME_OFF_VIEW',
            employerTimeOffViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employee Dashboard View
         * View Id: ezEmployeeDashboardView
         * Legacy View Ids: ezEmployeeDashboard, employeeDashboard, EMPLOYEE_DASHBOARD_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmployeeDashboardViewData = {
            name: 'Employee Dashboard',
            activeEnumName: 'ezEmployeeDashboardView',
            legacyEnumNames: [
                'ezEmployeeDashboard',
                'employeeDashboard',
                'EMPLOYEE_DASHBOARD_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYEE,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: true,
            employeeView: true,
            managerView: false,
            payrollManagerView: false,
            employerView: false,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployeeDashboardView',
            'ezEmployeeDashboardView',
            ezEmployeeDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployeeDashboard',
            'ezEmployeeDashboard',
            ezEmployeeDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employeeDashboard',
            'employeeDashboard',
            ezEmployeeDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYEE_DASHBOARD_VIEW',
            'EMPLOYEE_DASHBOARD_VIEW',
            ezEmployeeDashboardViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employee Schedule View
         * View Id: ezEmployeeSchedule
         * Legacy View Ids: employeeSchedule, EMPLOYEE_SCHEDULES_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmployeeScheduleViewData = {
            name: 'Employee Schedule View',
            activeEnumName: 'ezEmployeeSchedule',
            legacyEnumNames: [
                'employeeSchedule',
                'EMPLOYEE_SCHEDULES_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYEE,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: true,
            managerView: false,
            payrollManagerView: false,
            employerView: false,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployeeScheduleView',
            'ezEmployeeScheduleView',
            ezEmployeeScheduleViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employeeSchedule',
            'employeeSchedule',
            ezEmployeeScheduleViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYEE_SCHEDULES_VIEW',
            'EMPLOYEE_SCHEDULES_VIEW',
            ezEmployeeScheduleViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Employee Time Off View
         * View Id: ezEmployeeTimeOffView
         * Legacy View Ids: employeeTimeOffView, employeeTimeOff, EMPLOYEE_TIME_OFF_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezEmpoyeeTimeOffViewData = {
            name: 'Employee Time Off',
            activeEnumName: 'ezEmployeeTimeOffView',
            legacyEnumNames: [
                'employeeTimeOffView',
                'employeeTimeOff',
                'EMPLOYEE_TIME_OFF_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_EMPLOYEE,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: false,
            employeeView: true,
            managerView: false,
            payrollManagerView: false,
            employerView: false,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezEmployeeTimeOffView',
            'ezEmployeeTimeOffView',
            ezEmpoyeeTimeOffViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'employeeTimeOff',
            'employeeTimeOff',
            ezEmpoyeeTimeOffViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'EMPLOYEE_TIME_OFF_VIEW',
            'EMPLOYEE_TIME_OFF_VIEW',
            ezEmpoyeeTimeOffViewData);

        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Personal Dashboard View
         * View Id: ezPersonalDashboardView
         * Legacy View Ids: personalDashboardView, PERSONAL_DASHBOARD_VIEW
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        const ezPersonalDashboardViewData = {
            name: 'Personal Dashboard',
            activeEnumName: 'ezPersonalDashboardView',
            legacyEnumNames: [
                'personalDashboardView',
                'PERSONAL_DASHBOARD_VIEW'
            ],
            SUPPORTED_ROLES: [
                EzUserRole.ROLE_PERSONAL,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_ADMIN
            ],
            publicView: false,
            personalView: true,
            employeeView: false,
            managerView: false,
            payrollManagerView: false,
            employerView: false,
            supportView: true,
            adminView: true
        };
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'ezPersonalDashboardView',
            'ezPersonalDashboardView',
            ezPersonalDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'personalDashboardView',
            'personalDashboardView',
            ezPersonalDashboardViewData);
        this.#ezClockerViewIdEnumerationSingleton.ezBuildEnumProp(
            'PERSONAL_DASHBOARD_VIEW',
            'PERSONAL_DASHBOARD_VIEW',
            ezPersonalDashboardViewData);
    }

    /**
     * Static Initialization of EzExportAction
     */
    static {
        if (null == this.#ezClockerViewIdEnumerationSingleton) {
            this.#ezClockerViewIdEnumerationSingleton = new EzClockerViewId();

            globalThis.window.EzClockerViewId = this.#ezClockerViewIdEnumerationSingleton;

            this.#ezBuildEnumeration();
        }
    }

    /**
     * @public @constructor
     * >> DO NOT CREATE NEW INSTANCES <<
     * The static initializer will create a singleton instance that injects the static properties and methods
     * that you then use to access enumeration values.
     */
    constructor(name, value, optionalData) {
        super(name, value, optionalData);
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * End of Initialziation Section: !! DO NOT PLACE ANY CODE BEYOND THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}

globalThis.window.EzClockerViewId = EzClockerViewId;